import { Button, Popconfirm, Table } from 'antd'
import { InspectionArea } from 'features/libra/inspectionPlanning/planningTypes'
import {
  formatScope,
  scopeInUnit,
} from 'features/libra/inspectionPlanning/scopeMeasurement'
import { TechniqueCatalogue } from 'features/libra/inspectionPlanning/techniqueTypes'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { GeckoMeasurementSystem } from 'types'

import { ExecutionRowEditor } from './ExecutionRowEditor'
import { ExecutionColumn, ExecutionRow, ExecutionValue } from './executionTypes'
import {
  asMeasurement,
  inspectionResultBasisChanged,
  inspectionResultLinkExists,
  thicknessUnit,
} from './inspectionResultsModel'

function displayValue(
  column: ExecutionColumn,
  row: ExecutionRow,
  system: GeckoMeasurementSystem
) {
  const value = row.values[column.field]
  if (value === undefined || value === '') return '—'
  const scope = asMeasurement(value)
  if (column.type === 'thickness' && scope) {
    const unit = thicknessUnit(system === GeckoMeasurementSystem.Imperial)
    return `${scopeInUnit(scope, 'length', unit)?.toLocaleString('en-GB', { maximumFractionDigits: 4 }) ?? 'Incompatible units'} ${unit.name}`
  }
  if (column.type === 'scope' && scope && row.basis.method.scopeType)
    return formatScope(scope, row.basis.method.scopeType, system)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return typeof value === 'object' ? 'Incompatible units' : String(value)
}

export const ExecutionResultsTable = ({
  rows,
  columns,
  zones,
  areas,
  catalogue,
  rawZones,
  system,
  readonly,
  onChange,
  onRemove,
}: {
  rows: ExecutionRow[]
  columns: ExecutionColumn[]
  zones: ZoneParams[]
  areas: InspectionArea[]
  catalogue: TechniqueCatalogue
  rawZones: Record<string, unknown>[]
  system: GeckoMeasurementSystem
  readonly: boolean
  onChange: (id: string, field: string, value: ExecutionValue) => void
  onRemove: (id: string) => void
}) => (
  <Table<ExecutionRow>
    rowKey='id'
    size='small'
    pagination={false}
    dataSource={rows}
    scroll={{ x: 'max-content' }}
    columns={[
      { key: 'zone', title: 'Zone', render: (_, row) => row.basis.zoneTitle },
      {
        key: 'area',
        title: 'Inspection area / full zone',
        render: (_, row) => row.basis.areaTitle,
      },
      {
        key: 'method',
        title: 'Method',
        render: (_, row) => row.basis.method.method,
      },
      {
        key: 'required',
        title: 'Required coverage',
        render: (_, row) =>
          row.basis.requiredCoveragePercent === undefined
            ? '—'
            : `${row.basis.requiredCoveragePercent}%`,
      },
      {
        key: 'estimate',
        title: 'Estimated scope',
        render: (_, row) =>
          row.basis.method.scopeType
            ? formatScope(
                row.basis.method.estimatedScope,
                row.basis.method.scopeType,
                system
              )
            : '—',
      },
      ...columns.map((column) => ({
        key: column.field,
        title: column.label,
        render: (_: unknown, row: ExecutionRow) =>
          displayValue(column, row, system),
      })),
      ...(!readonly
        ? [
            {
              key: 'remove',
              title: '',
              render: (_: unknown, row: ExecutionRow) => (
                <Popconfirm
                  title='Remove this row?'
                  onConfirm={() => onRemove(row.id)}>
                  <Button danger size='small'>
                    Remove
                  </Button>
                </Popconfirm>
              ),
            },
          ]
        : []),
    ]}
    expandable={{
      expandedRowRender: (row) => (
        <ExecutionRowEditor
          row={row}
          columns={columns}
          system={system}
          sourceZone={
            rawZones.find((zone) => zone.id === row.sourceZoneId) ?? {}
          }
          linked={inspectionResultLinkExists(row, zones, areas)}
          basisChanged={inspectionResultBasisChanged(row, areas, catalogue)}
          readonly={readonly}
          onChange={(field, value) => onChange(row.id, field, value)}
        />
      ),
    }}
  />
)
